import { formatDateNumberShort } from '@seen/core/dates'
import { featureFlags } from '@seen/core/featureFlags'
import { ExtendedAllergy } from '@seen/core/models/allergies'
import * as patients from '@seen/core/models/patients'
import type { Participant } from '@seen/core/models/patients/participant'
import { DotSeparator } from '@seen/ui'
import { IconVirus } from '@tabler/icons-react'
import { orderBy } from 'lodash-es'
import { type JSX, useMemo, useRef, useState } from 'react'
import { toast } from 'sonner'
import { Dialog } from '#src/components/core/dialog/Dialog.tsx'
import { DialogContent } from '#src/components/core/dialog/DialogContent.tsx'
import { DialogHeader } from '#src/components/core/dialog/DialogHeader.tsx'
import { DialogTitle } from '#src/components/core/dialog/DialogTitle.tsx'
import { DialogDisplay } from '#src/components/core/dialog/display/DialogDisplay.tsx'
import { HoverCard } from '#src/components/core/hover-card/HoverCard.tsx'
import { HoverCardContent } from '#src/components/core/hover-card/HoverCardContent.tsx'
import { HoverCardTrigger } from '#src/components/core/hover-card/HoverCardTrigger.tsx'
import { TextLink } from '#src/components/core/TextLink.tsx'
import { ParticipantFlagBadge } from '#src/components/ParticipantFlagBadge.tsx'
import { AllergyCard } from '#src/components/patient/detail-content/AllergyCard.tsx'
import {
  AllergyForm,
  type AllergyFormValues,
} from '#src/components/patient/detail-content/AllergyForm.tsx'
import {
  FacesheetCard,
  FacesheetCardActions,
  FacesheetCardAddButton,
  FacesheetCardCollapseTrigger,
  FacesheetCardContent,
  FacesheetCardCount,
  FacesheetCardHeader,
  FacesheetCardIcon,
  FacesheetCardLabel,
  FacesheetCardTitle,
} from '#src/components/patient/detail-content/facesheet/index.ts'
import {
  FACESHEET_CARD_ROW_CLASS,
  FacesheetCardSection,
  FacesheetCardSections,
} from '#src/components/patient/detail-content/FacesheetCardSection.tsx'
import {
  CLINICAL_HOVER_CARD_WIDTH,
  useAdaptiveHoverCardPlacement,
} from '#src/hooks/useAdaptiveHoverCardPlacement.ts'
import { useCreateMutation } from '#src/hooks/useCreateMutation.ts'
import { useFeatureFlag } from '#src/hooks/useFeatureFlag.ts'
import { useIsReadOnly } from '#src/hooks/useIsReadOnly.ts'
import { useSearch } from '#src/hooks/useSearch.ts'

export interface AllergiesCardProps {
  participant: Participant
}

export function AllergiesCard({
  participant,
}: AllergiesCardProps): JSX.Element {
  const isReadOnly = useIsReadOnly()
  const nkda = participant.hasFlag(patients.PARTICIPANT_FLAGS.nkda.coding)

  const { data: allergies = [] } = useSearch(ExtendedAllergy, {
    patient: participant.reference,
    'clinical-status': 'active',
  })

  const { allergyAllergies, intoleranceAllergies } = useMemo(() => {
    const sortedAllergies = orderBy(
      allergies,
      (a) => (a.onsetDateTime ? new Date(a.onsetDateTime).getTime() : 0),
      ['desc']
    )

    const allergy = sortedAllergies.filter((a) => a.type === 'allergy')
    const intolerance = sortedAllergies.filter((a) => a.type === 'intolerance')

    return { allergyAllergies: allergy, intoleranceAllergies: intolerance }
  }, [allergies])

  const totalCount = allergyAllergies.length + intoleranceAllergies.length

  const [isAddAllergyDialogOpen, setIsAddAllergyDialogOpen] = useState(false)

  const createAllergy = useCreateMutation<ExtendedAllergy>({
    disableDefaultErrorToast: true,
    mutation: {
      onSuccess: () => {
        setIsAddAllergyDialogOpen(false)
        toast.success('Allergy created successfully')
      },
      onError: (cause: unknown) => {
        toast.error(cause instanceof Error ? cause.message : 'Unknown error')
      },
    },
  })

  const handleSubmit = (values: AllergyFormValues): void => {
    const allergy = new ExtendedAllergy({
      patient: participant.reference,
      code: {
        coding: [
          {
            // Persist the terminology the concept actually came from. The picker
            // unions SNOMED and RxNorm, so hardcoding SNOMED mislabeled every
            // RxNorm selection.
            system: values.allergy.system,
            code: values.allergy.code,
            display: values.allergy.display,
          },
        ],
        text: values.allergy.display,
      },
      clinicalStatus: {
        coding: [
          {
            system:
              'http://terminology.hl7.org/CodeSystem/allergyintolerance-clinical',
            code: values.status,
          },
        ],
      },
      verificationStatus: {
        coding: [
          {
            system:
              'http://terminology.hl7.org/CodeSystem/allergyintolerance-verification',
            code: 'confirmed',
          },
        ],
      },
      type: values.type,
      onsetDateTime: values.onsetDate ?? undefined,
      reaction:
        values.reaction || values.reactionCode
          ? [
              {
                manifestation: [
                  {
                    coding: values.reactionCode
                      ? [
                          {
                            system: 'http://snomed.info/sct',
                            code: values.reactionCode,
                          },
                        ]
                      : undefined,
                    text: values.reaction ?? '',
                  },
                ],
                severity: values.severity ?? undefined,
              },
            ]
          : undefined,
    })

    createAllergy.mutate(allergy)
  }

  return (
    <>
      <FacesheetCard collapsible={totalCount > 0} rumName="allergies">
        <FacesheetCardHeader>
          <FacesheetCardTitle>
            <FacesheetCardIcon>
              <IconVirus />
            </FacesheetCardIcon>
            <FacesheetCardLabel>
              <TextLink to={`${participant.relativeUrl}/allergies`}>
                Allergies
              </TextLink>
            </FacesheetCardLabel>
            <FacesheetCardCollapseTrigger />
          </FacesheetCardTitle>
          <FacesheetCardActions>
            {nkda && totalCount === 0 ? (
              <ParticipantFlagBadge flag={patients.PARTICIPANT_FLAGS.nkda} />
            ) : (
              <FacesheetCardCount>{totalCount}</FacesheetCardCount>
            )}
            {!isReadOnly && (
              <FacesheetCardAddButton
                aria-label="Add allergy"
                onClick={() => setIsAddAllergyDialogOpen(true)}
              />
            )}
          </FacesheetCardActions>
        </FacesheetCardHeader>
        {totalCount > 0 && (
          <FacesheetCardContent>
            <FacesheetCardSections className="text-xs">
              {allergyAllergies.length > 0 && (
                <FacesheetCardSection
                  title="Allergy"
                  count={allergyAllergies.length}
                >
                  {allergyAllergies.map((allergy) => (
                    <AllergyItem
                      key={allergy.id}
                      allergy={allergy}
                      participant={participant}
                    />
                  ))}
                </FacesheetCardSection>
              )}
              {intoleranceAllergies.length > 0 && (
                <FacesheetCardSection
                  title="Intolerance"
                  count={intoleranceAllergies.length}
                >
                  {intoleranceAllergies.map((allergy) => (
                    <AllergyItem
                      key={allergy.id}
                      allergy={allergy}
                      participant={participant}
                    />
                  ))}
                </FacesheetCardSection>
              )}
            </FacesheetCardSections>
          </FacesheetCardContent>
        )}
      </FacesheetCard>
      <Dialog
        open={isAddAllergyDialogOpen}
        onOpenChange={setIsAddAllergyDialogOpen}
      >
        <DialogDisplay>
          <DialogHeader>
            <DialogTitle>Add Patient Allergy</DialogTitle>
          </DialogHeader>
          <DialogContent>
            <AllergyForm
              onSubmit={handleSubmit}
              loading={createAllergy.isPending}
            />
          </DialogContent>
        </DialogDisplay>
      </Dialog>
    </>
  )
}

function AllergyItem({
  allergy,
  participant,
}: {
  allergy: ExtendedAllergy
  participant: Participant
}): JSX.Element {
  const viewportLayout = useFeatureFlag(featureFlags.hoverCardViewportLayout)
  const adaptivePlacement = useFeatureFlag(
    featureFlags.hoverCardAdaptivePlacement
  )
  const rowRef = useRef<HTMLParagraphElement>(null)
  const {
    side: adaptiveSide,
    sideOffset: adaptiveSideOffset,
    onOpenChange,
    cardRef,
  } = useAdaptiveHoverCardPlacement(rowRef, CLINICAL_HOVER_CARD_WIDTH)

  return (
    <HoverCard
      openDelay={300}
      closeDelay={200}
      onOpenChange={adaptivePlacement ? onOpenChange : undefined}
    >
      <HoverCardTrigger asChild>
        {/* Radix anchors to the trigger's border box. The row spans the
            column so long names wrap at the card width. Under side="bottom"
            that left edge never moves; adaptive placement still needs the
            full column to measure the right edge. */}
        <p ref={rowRef} className={FACESHEET_CARD_ROW_CLASS}>
          <TextLink to={`${participant.relativeUrl}/allergies/${allergy.id}`}>
            {allergy.name}
          </TextLink>
          {allergy.onsetDateTime && (
            <span className="select-none">
              <DotSeparator />
              <span className="text-stone-500">
                {formatDateNumberShort(allergy.onsetDateTime)}
              </span>
            </span>
          )}
        </p>
      </HoverCardTrigger>
      <HoverCardContent
        ref={adaptivePlacement ? cardRef : undefined}
        side={
          adaptivePlacement ? adaptiveSide : viewportLayout ? 'bottom' : 'right'
        }
        sideOffset={adaptivePlacement ? adaptiveSideOffset : undefined}
        align="start"
        scrollChainAnchor={adaptivePlacement ? rowRef : undefined}
        className="w-auto"
      >
        <AllergyCard
          allergy={allergy}
          to={`${participant.relativeUrl}/allergies/${allergy.id}`}
        />
      </HoverCardContent>
    </HoverCard>
  )
}
